<template>
  <question-base-wrapper
    v-bind="baseWrapperProps"
    :help-text="defaultHelpText"
  >
    <validation-provider
      v-slot="{ failed, errors }"
      name="Property Build Sums Ins"
      :vid="`${stepId}_${questionId}`"
      rules="propertyRebuildCost"
      slim
    >
      <help-text
        v-if="!isColumnLayout && isBCISHelpTextShown"
        class="mb-6"
        :error="formObserver.validated && failed"
      >
        <house-rebuild-help-text
          :rooms="rooms"
          :rebuild-cost="rebuildCost"
        />
      </help-text>
      <currency-input
        v-model.number="questionData[customFields.field]"
        hide-details="auto"
        :error="failed"
        :maxlength="inputCharsLimiters.BUILD_SUMS_INS_MAX"
        :error-messages="errors"
        data-test="home_build_sums_ins"
        placeholder="e.g. 200000"
        prefix="£"
        outlined
      />
    </validation-provider>
    <!-- [SU-5906] - don't show BCIS image for apricot  -->
    <v-img
      v-show="rebuildCost && !constants.PARTNERS.Apricot.includes(config.partner)"
      contain
      class="mt-6 ml-auto BCIS-logo"
      max-height="42"
      max-width="120"
      :src="require('@/assets/BCIS_logo.png')"
    />
    <template
      v-if="isColumnLayout && isBCISHelpTextShown"
      #subsection="{ observer }"
    >
      <help-text
        :error="formObserver.validated && observer.failed"
      >
        <house-rebuild-help-text
          :rooms="rooms"
          :rebuild-cost="rebuildCost"
        />
      </help-text>
    </template>
  </question-base-wrapper>
</template>

<script>
import QuestionBase from '@/components/questions/Base.vue';
import QuestionBaseWrapper from '@/components/questions/BaseWrapper.vue';
import HouseRebuildHelpText from '@/components/home/HouseRebuildHelpText/HouseRebuildHelpText.vue';
import {
  pick,
} from 'lodash';
import {
  ref,
  watch,
  inject,
  computed,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api';
import { validate } from 'vee-validate';

export default {
  name: 'question-build-sums-ins',
  extends: QuestionBase,
  components: {
    QuestionBaseWrapper,
    HouseRebuildHelpText,
  },
  props: {
    prerequisites: {
      type: Array,
      default: () => [],
    },
    /*
      bedroomsQuestionKey - key of the bedroom's question. We need to know it in order to display correct amount
      of rooms in the help text
    */
    bedroomsQuestionKey: {
      type: String,
      default: 'bedrooms',
    },
  },
  setup (props, ctx) {
    const instance = getCurrentInstance();
    const productName = inject('productName');
    const isLeadgen = inject('isLeadgen');
    const config = inject('config');
    const constants = inject('constants');
    const helpers = inject('helpers');
    const prerequisiteFields = props.prerequisites.map(item => item.field) || [];
    const rebuildCost = ref(null);

    // COMPUTED
    const fieldsData = computed(() => isLeadgen ? props.formData : props.stepData);
    const getFieldsForHouseRebuildHandler = computed(() => {
      const result = pick(fieldsData.value, prerequisiteFields);
      if (productName === 'home') {
        result.propertyTotalRooms = propertyTotalRooms.value;
      }

      return result;
    });
    // 'home' has multiple fields for number of bedrooms, so it calculates in computed separately
    const propertyTotalRooms = computed(() => {
      return fieldsData.value?.propertyBedrooms + fieldsData.value?.propertyBathrooms + fieldsData.value?.propertyLivingrooms + fieldsData.value?.propertyOtherooms;
    });
    const rooms = computed(() => {
      if (productName === 'home') {
        return propertyTotalRooms.value;
      }
      return fieldsData.value?.[props.bedroomsQuestionKey || 'bedrooms'];
    });
    // use help text from the configs, until we get BCIS response
    const defaultHelpText = computed(() => {
      // [SU-5906] - show only default help text for Apricot
      if (constants.PARTNERS.Apricot.includes(config.partner)) return instance.props.helpText;
      return Object.keys(rebuildCost.value || {}).length === 0 ? instance.props.helpText : null;
    });
    // show BCIS help text only when we get response from BE
    const isBCISHelpTextShown = computed(() => {
      // [SU-5906] - don't show BCIS block for apricot
      if (constants.PARTNERS.Apricot.includes(config.partner)) return false;
      return Object.keys(rebuildCost.value || {}).length;
    });

    // METHODS
    const isPrerequisitesValid = async (values) => {
      for (const key of Object.keys(values)) {
        const fieldRules = props.prerequisites.find((item) => item.field === key)?.validationRules;
        const value = values[key];

        if (fieldRules) {
          const result = await validate(value, helpers.parseRules(fieldRules, props.stepData));
          if (!result.valid) {
            return false;
          }
        }
      }
      return true;
    };
    const houseRebuildHandler = async (newValues, prevValues) => {
      const isValuesChanged = JSON.stringify(newValues) !== JSON.stringify(prevValues);
      if (!isValuesChanged) return;

      const prerequisitesValid = await isPrerequisitesValid(newValues);

      if (!prerequisitesValid && !!instance.proxy.questionData[props.customFields.field]) {
        // reset field value and BCIS help text if it was filled before
        rebuildCost.value = null;
        instance.proxy.questionData[props.customFields.field] = null;
      }

      if (!prerequisitesValid) return;

      await getBCIS();
    };
    const init = () => {
      const buildingCost = instance.proxy.questionData[props.customFields.field];

      const hasCostRebuildProperty = !!buildingCost;

      watch(getFieldsForHouseRebuildHandler, houseRebuildHandler, {
        immediate: !hasCostRebuildProperty,
      });
    };
    const processBCISResponse = (microData) => {
      const {
        build_expected_total: expectedFormatted,
        build_minimum_total: min,
        build_maximum_total: max,
        build_sums_ins: expected,
      } = microData;

      return {
        expectedFormatted,
        expected,
        min,
        max,
      };
    };
    /**
     * Get building cost from microservice
     * https://wiki.quotezone.co.uk/w/index.php/Laravel_Microservice#House_Rebuild_Value_.28RICS.29
     * @returns {Promise<void>}
     */
    const getBCIS = async () => {
      const buildingCost = instance.proxy.questionData[props.customFields.field];
      // Core products checks session data, LGs take fields data from request
      if (!isLeadgen) {
        const journeyPayload = {
          productName: productName,
          productPartnerId: config.partner,
          step: props.stepId,
          quoteData: fieldsData.value,
        };

        await instance.proxy.$api.processJourney(journeyPayload);
      }
      try {
        const userInputs = prerequisiteFields.reduce((acc, field) => {
          return {
            ...acc,
            [field]: fieldsData.value[field],
          };
        }, {});
        const { status, data } = await instance.proxy.$api.getHouseRebuildValue(userInputs);
        if (status === 'success' && data) {
          const response = processBCISResponse(data);

          rebuildCost.value = response;
          const expectedValue = Number(response.expected);
          // show FYI message if house rebuild changes after it was settled
          if (buildingCost && buildingCost !== expectedValue) {
            ctx.emit('toast', {
              type: 'info',
              text: 'Please note: Changes to your answers here have updated your property rebuild value.',
            });
          }
          instance.proxy.questionData[props.customFields.field] = expectedValue;
        }
      } catch (err) {
        rebuildCost.value = null;
        console.log(err.toString());
      }
    };

    onMounted(() => {
      init();
    });

    return {
      rooms,
      isLeadgen,
      rebuildCost,
      defaultHelpText,
      isBCISHelpTextShown,
    };
  },
};
</script>
